import React, {
  memo, useCallback, useEffect, useMemo, useRef, useState,
} from 'react'

import {
  FiltersSidebar,
  Icon,
  Button,
  ChevronDown,
  Close,
  MoreFilters as MoreFiltersIcon,
  Search,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { createPortal } from 'react-dom'

import FileFormatContent from 'Pods/SearchPage/Components/MainFilters/Components/FileFormat/FileFormatContent'
import LicenseContent from 'Pods/SearchPage/Components/MainFilters/Components/License/LicenseContent'
import PriceContent from 'Pods/SearchPage/Components/MainFilters/Components/Price/PriceContent'
import { addFileType, removeFileType, clearFileTypes } from 'Pods/SearchPage/Store/actions/fileTypes'
import { clearLicenses } from 'Pods/SearchPage/Store/actions/licenses'
import {
  addAdvancedFilter,
  removeAdvancedFilter,
  setContentFilter,
  setPersonalFilter,
  setPurchaseFilter,
  clearMoreFilters,
} from 'Pods/SearchPage/Store/actions/moreFilters'
import { clearPricing } from 'Pods/SearchPage/Store/actions/pricing'
import { LICENSE_TYPE } from 'Pods/SearchPage/Store/constants'
import { useSearchPageStore } from 'Pods/SearchPage/Store/index'
import FiltersWrapper from 'Pods/Shared/FiltersWrapper/FiltersWrapper'
import I18n from 'Pods/Shared/I18n'

import { EMPTY_ARRAY } from 'Utils/stableDefaults'
import useBreakpoint from 'Utils/useBreakpoint'

import CollapsibleSection from './CollapsibleSection'
import {
  advancedFiltersConfig,
  contentFiltersConfig,
  personalFiltersConfig,
  purchaseFiltersConfig,
} from './constants'

const translate = (key) => I18n.t(key, { ns: 'rails' })

const prefixIcon = <Icon notSpaced svgComponent={MoreFiltersIcon} color="dark" size="small" />
const headerIcon = <Icon notSpaced svgComponent={MoreFiltersIcon} color="dark" size="small" />
const closeIcon = <Icon notSpaced svgComponent={Close} />

const trailIcon = (
  <Icon
    notSpaced
    svgComponent={ChevronDown}
    color="darker"
    className="more-filters__trigger-chevron"
  />
)

// Below md the bar collapses, so the filters open in a full-screen modal behind a "Filters" button.
const COMPACT_BREAKPOINTS = ['xs', 'sm']
const DEFAULT_LICENSES = [LICENSE_TYPE.ALL]

// Modal starts where the fixed app navbar ends; measured on open so it adapts to the
// sales-off bar and breakpoint-specific navbar heights.
const getNavbarOffset = () => {
  const nav = typeof document !== 'undefined' && document.querySelector('.wayfinder')
  return nav ? Math.round(nav.getBoundingClientRect().bottom) : 0
}

function MoreFilters() {
  const advancedFilters = useSearchPageStore(state => state.advancedFilters)
  const contentFilter = useSearchPageStore(state => state.contentFilter)
  const personalFilter = useSearchPageStore(state => state.personalFilter)
  const purchaseFilter = useSearchPageStore(state => state.purchaseFilter)
  const fileTypes = useSearchPageStore(state => state.fileTypes ?? EMPTY_ARRAY)
  const onSale = useSearchPageStore(state => state.onSale)
  const withSubscription = useSearchPageStore(state => state.withSubscription)
  const priceRanges = useSearchPageStore(state => state.priceRanges ?? EMPTY_ARRAY)
  const minPrice = useSearchPageStore(state => state.minPrice ?? '')
  const maxPrice = useSearchPageStore(state => state.maxPrice ?? '')
  const licenses = useSearchPageStore(state => state.licenses ?? DEFAULT_LICENSES)
  const aiUsageAllowed = useSearchPageStore(state => state.aiUsageAllowed ?? false)
  const breakpoint = useBreakpoint()
  // Gate the popover/modal split until mounted so the first client render matches SSR
  // (useBreakpoint is undefined on the server), avoiding a hydration mismatch.
  const [hasMounted, setHasMounted] = useState(false)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [navOffset, setNavOffset] = useState(0)
  const drawerRef = useRef(null)
  // Element that had focus when the drawer opened, so it can be restored on close.
  const triggerElementRef = useRef(null)

  useEffect(() => {
    setHasMounted(true)
  }, [])

  const isCompact = hasMounted && COMPACT_BREAKPOINTS.includes(breakpoint)

  const handleCloseDrawer = useCallback(() => {
    setIsDrawerOpen(false)
  }, [])

  useEffect(() => {
    if (!isCompact && isDrawerOpen) {
      setIsDrawerOpen(false)
    }
  }, [isCompact, isDrawerOpen])

  // Modal a11y: focus the drawer on open, trap Tab inside it, close on Escape, and restore
  // focus to the trigger on close.
  useEffect(() => {
    if (!isDrawerOpen) return undefined

    const drawer = drawerRef.current
    const previouslyFocused = triggerElementRef.current
    const getFocusable = () => (drawer
      ? Array.from(drawer.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      ))
      : [])

    const focusable = getFocusable()
    if (focusable.length > 0) {
      focusable[0].focus()
    } else if (drawer) {
      drawer.focus()
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        handleCloseDrawer()
        return
      }
      if (event.key !== 'Tab') return
      const items = getFocusable()
      if (items.length === 0) return
      const first = items[0]
      const last = items[items.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus()
      }
    }
  }, [isDrawerOpen, handleCloseDrawer])

  // Filters owned by the More Filters popover itself; drives the desktop trigger and its Reset.
  const hasOwnFilters = useMemo(() => {
    return (
      advancedFilters.length > 0 ||
      contentFilter != null ||
      personalFilter != null ||
      purchaseFilter != null
    )
  }, [advancedFilters, contentFilter, personalFilter, purchaseFilter])

  // On compact breakpoints the trigger opens the All Filters modal, which also hosts
  // File Format and Price — those count toward the trigger's active state there only.
  const hasModalFilters = useMemo(() => {
    return (
      hasOwnFilters ||
      fileTypes.length > 0 ||
      onSale ||
      withSubscription ||
      priceRanges.length > 0 ||
      minPrice !== '' ||
      maxPrice !== '' ||
      licenses[0] !== LICENSE_TYPE.ALL ||
      aiUsageAllowed
    )
  }, [
      hasOwnFilters,
      fileTypes,
      onSale,
      withSubscription,
      priceRanges,
      minPrice,
      maxPrice,
      licenses,
      aiUsageAllowed,
    ])

  const handleReset = useCallback(() => {
    clearMoreFilters()
  }, [])

  const handleFileTypeChange = useCallback((_, value, isOn) => {
    if (isOn) {
      addFileType(value)
    } else {
      removeFileType(value)
    }
  }, [])

  const handleModalReset = useCallback(() => {
    clearFileTypes()
    clearPricing()
    clearLicenses()
  }, [])

  const modalSearchTextInputProps = useMemo(() => ({
    leadIcon: <Icon notSpaced svgComponent={Search} color="dark" />,
    textInputClassName: 'filter-wrapper__search-input',
  }), [])

  const handleAdvancedFilterChange = useCallback((_, value, isOn) => {
    if (isOn) {
      addAdvancedFilter(value)
    } else {
      removeAdvancedFilter(value)
    }
  }, [])

  const handleContentFilterChange = useCallback((_, value) => {
    setContentFilter(value)
  }, [])

  const handlePersonalFilterChange = useCallback((_, value) => {
    setPersonalFilter(value)
  }, [])

  const handlePurchaseFilterChange = useCallback((_, value) => {
    setPurchaseFilter(value)
  }, [])

  const handleOpenDrawer = useCallback(() => {
    triggerElementRef.current = document.activeElement
    setNavOffset(getNavbarOffset())
    setIsDrawerOpen(true)
  }, [])

  const selectedContentFilter = useMemo(() => {
    return contentFilter != null ? [contentFilter] : []
  }, [contentFilter])

  const selectedPersonalFilter = useMemo(() => {
    return personalFilter != null ? [personalFilter] : []
  }, [personalFilter])

  const selectedPurchaseFilter = useMemo(() => {
    return purchaseFilter != null ? [purchaseFilter] : []
  }, [purchaseFilter])

  const content = useMemo(() => (
    <>
      <FiltersSidebar
        isCollapsible={false}
        filters={advancedFiltersConfig}
        selectedFilters={advancedFilters}
        onFiltersChange={handleAdvancedFilterChange}
        isControlled
        useNewCheckboxes
        hideShowMore
        baseFormWrapperClassName="filter-wrapper__content"
      />
      <FiltersSidebar
        isCollapsible={false}
        filters={contentFiltersConfig}
        selectedFilters={selectedContentFilter}
        onFiltersChange={handleContentFilterChange}
        hideShowMore
        baseFormWrapperClassName="filter-wrapper__content"
        useRadio
      />
      <FiltersSidebar
        isCollapsible={false}
        filters={personalFiltersConfig}
        selectedFilters={selectedPersonalFilter}
        onFiltersChange={handlePersonalFilterChange}
        hideShowMore
        baseFormWrapperClassName="filter-wrapper__content"
        useRadio
      />
      <FiltersSidebar
        isCollapsible={false}
        filters={purchaseFiltersConfig}
        selectedFilters={selectedPurchaseFilter}
        onFiltersChange={handlePurchaseFilterChange}
        hideLastDivider
        hideShowMore
        baseFormWrapperClassName="filter-wrapper__content"
        useRadio
      />
      <Button
        type="ghost"
        size="tiny"
        customClass="filter-wrapper__reset-button"
        onClick={handleReset}
        disabled={!hasOwnFilters}
      >
        {translate('search_page.filters.more_filters.reset')}
      </Button>
    </>
  ), [
    advancedFilters,
    selectedContentFilter,
    selectedPersonalFilter,
    selectedPurchaseFilter,
    handleAdvancedFilterChange,
    handleContentFilterChange,
    handlePersonalFilterChange,
    handlePurchaseFilterChange,
    handleReset,
    hasOwnFilters,
  ])

  if (isCompact) {
    return (
      <>
        <Button
          type="secondary"
          variant="with-icon"
          icon={prefixIcon}
          isActive={hasModalFilters}
          onClick={handleOpenDrawer}
          customClass="filter-wrapper__button more-filters__trigger"
          aria-label={translate('search_page.filters.more_filters.trigger_label')}
          aria-expanded={isDrawerOpen}
          trailIcon={trailIcon}
        >
          <span className="more-filters__trigger-label">
            {translate('search_page.filters.more_filters.trigger_label')}
          </span>
        </Button>
        {isDrawerOpen && createPortal(
          // Portaled to body so the full-screen modal escapes the filter bar's stacking
          // context (.search-page__main-filters { z-index: 2 }). It starts below the fixed
          // navbar via the measured offset, leaving the navbar visible.
          <div
            ref={drawerRef}
            className="more-filters__drawer"
            role="dialog"
            aria-modal="true"
            aria-label={translate('search_page.filters.more_filters.modal_title')}
            tabIndex={-1}
            style={{ top: navOffset }}
          >
            <div className="more-filters__drawer-header">
              <div className="more-filters__drawer-title">
                {headerIcon}
                <span>{translate('search_page.filters.more_filters.modal_title')}</span>
              </div>
              <Button
                type="ghost"
                variant="only-icon"
                icon={closeIcon}
                onClick={handleCloseDrawer}
                aria-label={translate('search_page.filters.more_filters.close_label')}
              />
            </div>
            <div className="more-filters__drawer-body">
              <CollapsibleSection title={translate('search_page.filters.file_format.title')}>
                <FileFormatContent
                  fileTypes={fileTypes}
                  onFilterChange={handleFileTypeChange}
                  textInputProps={modalSearchTextInputProps}
                  limit={3}
                  hideShowMore={false}
                />
              </CollapsibleSection>
              <CollapsibleSection title={translate('search_page.filters.price.title')}>
                <PriceContent />
              </CollapsibleSection>
              <CollapsibleSection title={translate('search_page.filters.license.title')}>
                <LicenseContent hideGroupLabels />
              </CollapsibleSection>
            </div>
            <div className="more-filters__drawer-footer">
              <Button
                type="secondary"
                customClass="more-filters__footer-button"
                onClick={handleModalReset}
              >
                {translate('search_page.filters.more_filters.reset')}
              </Button>
              <Button
                type="primary"
                customClass="more-filters__footer-button"
                onClick={handleCloseDrawer}
              >
                {translate('search_page.filters.more_filters.apply_filters')}
              </Button>
            </div>
          </div>,
          document.body,
        )}
      </>
    )
  }

  return (
    <FiltersWrapper
      placeholder={translate('search_page.filters.more_filters.desktop_title')}
      prefixIcon={prefixIcon}
      content={content}
      hasSelectedAnyFilters={hasOwnFilters}
      closingDropdownClassName="filter-wrapper"
    />
  )
}

export default memo(MoreFilters)
